import React from 'react';
import {
  FaAppleAlt,
  FaBalanceScale,
  FaBook,
  FaBriefcase,
  FaCar,
  FaCashRegister,
  FaChartLine,
  FaCoffee,
  FaFileInvoiceDollar,
  FaGem,
  FaGift,
  FaGlobe,
  FaHammer,
  FaPlug,
  FaShoppingCart,
  FaStore,
  FaWarehouse,
} from 'react-icons/fa';
import ProductPageTemplate from '../../components/productPageTemplate';

const data = {
  theme: {
    accent: '#3B9ED4',
    accentSoft: '#6BBDDF',
    darkBg: '#173042',
    darkBgSoft: '#2178AB',
  },
  hero: {
    eyebrows: ['ERP em nuvem', 'Fiscal, caixa e PDV', '+40 tipos de negócio'],
    accent: 'ERP',
    title: 'que centraliza venda, fiscal e operação diária.',
    lead:
      'O ECOSIS Web organiza a rotina comercial em uma base só, conectando documentos fiscais, pedidos, estoque, financeiro e frente de caixa.',
    support:
      'A proposta aqui é reduzir retrabalho e manter a empresa rodando com visão operacional clara, seja no comércio, serviços ou pequenas operações de transporte.',
    metrics: [
      'NFe, NFCe, NFSe, CTe, CTeOS e MDFe',
      'Vendas, estoque e financeiro no mesmo fluxo',
      'Feito para pequeno e médio varejo e serviços',
    ],
    primaryCta: { label: 'Ver módulos', href: '#modulos' },
    secondaryCta: { label: 'Falar com a equipe', href: '#contato-produto' },
    visualCards: [
      { label: 'Documentos fiscais', value: 'Fluxo completo' },
      { label: 'Caixa e vendas', value: 'Tudo integrado' },
    ],
    panelTitle: 'Painel operacional',
    panelItems: [
      {
        label: 'Fiscal',
        title: 'Emissões e validações concentradas',
        meta: 'Documentos, consulta na SEFAZ e aderência à legislação no mesmo ambiente.',
      },
      {
        label: 'Vendas',
        title: 'Pedido, orçamento e ordem de serviço',
        meta: 'Da venda rápida à operação externa, com contratos, romaneios e compras.',
      },
      {
        label: 'Caixa',
        title: 'Financeiro conectado à operação',
        meta: 'Contas, conciliação bancária, boletos e integração direta com a venda.',
      },
    ],
  },
  intro: {
    tag: 'Visão geral',
    title: 'Uma base única para vender, emitir, controlar e acompanhar.',
    lead:
      'O produto atende empresas que precisam unir obrigação fiscal, operação comercial e leitura gerencial sem fragmentar o dia a dia em vários sistemas.',
    cardTitle: 'Estrutura pensada para rotina real',
    cardText:
      'Em vez de tratar fiscal, estoque e caixa como blocos separados, o ECOSIS Web trabalha com um fluxo operacional contínuo: vende, registra, baixa, concilia e acompanha.',
    quickPoints: [
      'Emissão fiscal adaptada a diferentes documentos e cenários operacionais.',
      'Venda, orçamento, ordem de serviço e compras com menos troca de contexto.',
      'Gestão financeira e estoque conectadas ao que acontece na ponta.',
    ],
  },
  modulesSection: {
    tag: 'Módulos principais',
    title: 'O núcleo do ECOSIS Web foi organizado em frentes objetivas de operação.',
    lead:
      'A estrutura abaixo reflete os blocos funcionais que já existiam na página anterior, agora com leitura mais direta e hierarquia mais profissional.',
  },
  modules: [
    {
      icon: FaFileInvoiceDollar,
      title: 'Documentos fiscais',
      description: 'Emita documentos de forma descomplicada para manter a operação regularizada sem deslocar o time para rotinas paralelas.',
      bullets: ['NFe, NFCe e NFSe', 'CTe, CTeOS e MDFe', 'Rotina fiscal mais centralizada'],
    },
    {
      icon: FaBalanceScale,
      title: 'Fiscal e contábil',
      description: 'Apoia empresas em diferentes regimes com uma base voltada para conformidade e organização do fechamento.',
      bullets: ['MEI, ME, Simples, Lucro Real e Presumido', 'Sintegra e SPED', 'Contribuições, Bloco K e inventário'],
    },
    {
      icon: FaShoppingCart,
      title: 'Controle de vendas',
      description: 'Gerencie o ciclo comercial do pedido à entrega com recursos para operação interna e externa.',
      bullets: ['Pedido de venda e ordem de serviço', 'Orçamento, condicional e venda futura', 'Compras, contratos e romaneios'],
    },
    {
      icon: FaCashRegister,
      title: 'Financeiro e caixa',
      description: 'Conecte contas, conciliações e boletos ao que a empresa realmente vende e entrega no dia a dia.',
      bullets: ['Contas a pagar e receber', 'Conciliação bancária', 'Boletos integrados com banco'],
    },
    {
      icon: FaStore,
      title: 'Frente de caixa e PDV',
      description: 'A ponta de venda trabalha integrada ao restante da operação, acelerando atendimento e controle.',
      bullets: ['Controle de caixa', 'Emissão de NFCe', 'Venda rápida e força de vendas'],
    },
    {
      icon: FaWarehouse,
      title: 'Estoque e produção',
      description: 'Monitore entradas, saídas e processos produtivos com apoio para etiquetagem e organização física.',
      bullets: ['Controle de produção', 'Emissão de etiquetas', 'Fluxo ligado às vendas e notas'],
    },
    {
      icon: FaChartLine,
      title: 'Gestão inteligente',
      description: 'Leia a operação com dashboards, relatórios e gestão documental sem depender de extrações manuais constantes.',
      bullets: ['Dashboards fáceis de interpretar', 'Relatórios operacionais', 'Gestão e assinatura de documentos'],
    },
    {
      icon: FaPlug,
      title: 'Integrações',
      description: 'Amplie a operação com canais e meios de pagamento que conversam com o ERP.',
      bullets: ['PIX, TEF e SAT', 'WhatsApp', 'SEUPOS e Connect'],
    },
  ],
  operations: {
    tag: 'Onde encaixa melhor',
    title: 'Uma solução horizontal para negócios que precisam operar com base única.',
    lead:
      'A página antiga listava muitos tipos de operação atendidos. O novo bloco mantém esse alcance, mas com leitura mais limpa.',
    audienceTitle: 'Setores atendidos',
    audienceText:
      'O ECOSIS Web foi desenhado para operações comerciais e de serviço que precisam unir emissão, caixa, estoque e gestão sem inflar a estrutura.',
    sectors: [
      { icon: FaBriefcase, label: 'Prestadores de serviços' },
      { icon: FaBook, label: 'Livrarias e papelarias' },
      { icon: FaShoppingCart, label: 'Mercados e açougues' },
      { icon: FaCoffee, label: 'Panificadoras e cafeterias' },
      { icon: FaGem, label: 'Joalherias e óticas' },
      { icon: FaGift, label: 'Presentes e variedades' },
      { icon: FaHammer, label: 'Construção e ferragens' },
      { icon: FaCar, label: 'Autopeças e oficinas' },
      { icon: FaAppleAlt, label: 'Horti-fruti e verdureira' },
      { icon: FaGlobe, label: 'E muitos outros' },
    ],
    stat: {
      value: '+40',
      text: 'Tipos de negócios atendidos do pequeno ao médio varejo, com operação fiscal e comercial no mesmo sistema.',
    },
    cards: [
      {
        title: 'Painel do contador',
        text: 'A página original destacava o painel gratuito do contador como apoio para reduzir fricção entre operação e contabilidade.',
        bullets: ['Acesso mais direto às rotinas fiscais', 'Mais previsibilidade no fechamento'],
      },
      {
        title: 'SEFAZ e averbação',
        text: 'O sistema também cobre consultas fiscais e processos complementares de validação e averbação.',
        bullets: ['Consulta de notas na SEFAZ', 'Apoio à operação regulatória'],
      },
      {
        title: 'SEUPOS e Connect',
        text: 'Recursos complementares para pagamentos e frente de caixa ampliam a capacidade operacional sem criar outro fluxo isolado.',
        bullets: ['Stone, Rede e Mercado Pago', 'Vero e Cielo no Connect'],
      },
      {
        title: 'Gestão do negócio',
        text: 'Além da parte transacional, a solução também abre espaço para catálogo digital e gestão mais ampla do negócio.',
        bullets: ['Catálogo digital', 'Leitura gerencial mais rápida'],
      },
    ],
  },
  contact: {
    tag: 'Contato',
    title: 'Quer avaliar se o ECOSIS Web cobre sua operação atual?',
    lead: 'A equipe pode mapear seu fluxo comercial e indicar como a implantação encaixa no seu cenário.',
  },
};

function EcosisWeb() {
  return <ProductPageTemplate data={data} />;
}

export default EcosisWeb;
